import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CloudFile } from '../../../../_models/files';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as fileSaver from 'file-saver';
import { UserProfile } from '../../../../_models/user-profile';
import { UserProfileService } from '../../../../_services/user-profile.service';
import { PortalApiService } from '../../../../_services/portal-api.service';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-file-view-edit',
  templateUrl: './file-view-edit.component.html',
  styleUrls: ['./file-view-edit.component.css'],
  providers: [MessageService]
})
export class FileViewEditComponent {
  @Input() file: CloudFile;
  @Input() storageType: string;
  @Input() wellID?: number;
  @Input() expenseID?: number;
  @Input() disableEdit?: boolean;
  @Input() disableImgPreview?: boolean;
  @Input() tableDisplay?: boolean;
  @Output() fileDeleteSucceeded: EventEmitter<CloudFile> = new EventEmitter();
  @Output() fileUpdateSucceeded: EventEmitter<CloudFile> = new EventEmitter();
  userProfile: UserProfile;
  editForm: FormGroup;
  errorEditMessage: string;
  editing: boolean = false;
  errorDeleteMessage: string;
  deleting: boolean = false;
  viewFileModalVisible: boolean = false;
  editFileDataModalVisible: boolean = false;
  deleteFileModalVisible: boolean = false;
  private onDestroy$ = new Subject<void>();

  constructor(
    private apiService: PortalApiService,
    private fb: FormBuilder,
    private userService: UserProfileService,
    private messageService: MessageService,
    private sanitizer: DomSanitizer,
  ) {
    this.userService.userProfile$.pipe(takeUntil(this.onDestroy$)).subscribe(currentProfile => { this.userProfile = currentProfile });
  }

  ngOnInit(): void {

  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  dismissAllModals() {
    this.viewFileModalVisible = false;
    this.editFileDataModalVisible = false;
    this.deleteFileModalVisible = false;
  }


  initializeFileForm() {
    this.editForm = this.fb.group({
      fileName: [this.file.fileName],
      fileUrl: [this.file.fileUrl],
      fileType: [this.file.fileType],
      description: [this.file.description],
      createdByID: [this.file.createdByID],
      createdOn: [this.file.createdOn],
      lastUpdateByID: [this.file.lastUpdateByID],
      lastUpdateTime: [this.file.lastUpdateTime],
      loading: [this.file.loading],
      downloading: [this.file.downloading],
      error: [this.file.error],
      isAttachment: [this.file.isAttachment],
      size: [this.file.size]
    });
  }

  editFile() {
    this.initializeFileForm();
    if (this.disableImgPreview) {
      if (!this.file.fileUrl) {
        this.retrieveFileUrlForImage();
      }
    }
    this.editing = false;
    this.errorEditMessage = null;
    this.editFileDataModalVisible = true;
  }

  handleToggleIsAttachment(value: boolean) {
    this.editForm.patchValue({
      "isAttachment": value
    });
    this.editForm.markAsDirty();
  }

  onSubmitFileEdit() {
    this.errorEditMessage = null;
    this.editing = true;
    this.editForm.patchValue({
      lastUpdateByID: this.userProfile.id
    });

    switch (this.storageType) {
      case 'well':
        this.callApiForUpdateWellFile();
        break;
      default:
        break;
    }
  }

  callApiForUpdateWellFile() {
    this.apiService.updateFileDataForWell(this.wellID, this.file.fileName, this.editForm.value).subscribe({
      next: data => {
        this.fileUpdateSucceeded.emit(this.editForm.value);
        this.editing = false;
        this.dismissAllModals();
      },
      error: err => {
        console.log(err);
        this.editing = false;
        this.errorEditMessage = err.error || 'An error occurred. The file failed to update properly.';
      }
    })
  }

  viewFile() {
    if (this.disableImgPreview) {
      if (!this.file.fileUrl) {
        this.retrieveFileUrlForImage();
      }
    }
    this.viewFileModalVisible = true;
  }

  downloadFile() {
    this.file.downloading = true;
    switch (this.storageType) {
      case 'well':
        return this.apiService.downloadFileForWell(this.wellID, this.file.fileName).subscribe({
          next: (result: any) => {
            if (result.type != 'text/plain') {
              var blob = new Blob([result]);
              fileSaver(blob, this.file.fileName);
              this.file.downloading = false;
            }
            else {
              this.file.downloading = false;
              this.displayToastMessage(false, "An error occurred. Failed to download file.")
            }
          },
          error: err => {
            console.log(err);
            this.file.downloading = false;
            this.displayToastMessage(false, "An error occurred. Failed to download file.")
          }
        });
      default:
        break;
    }
  }

  retrieveFileUrlForImage() {
    this.file.loading = true;
    if (this.file.fileType == 'image') {
      return this.apiService.downloadFileForWell(this.wellID, this.file.fileName).subscribe({
        next: (result: any) => {
          if (result.type != 'text/plain') {
            let objectURL = URL.createObjectURL(result);
            this.file.fileUrl = this.sanitizer.bypassSecurityTrustUrl(objectURL);
            this.file.loading = false;
          }
          else {
            this.file.loading = false;
            this.file.error = true;
          }
        },
        error: err => {
          console.log(err);
          this.file.loading = false;
          this.file.error = true;
        }
      });
    }
  }


  deleteFile() {
    this.errorDeleteMessage = null;
    this.deleting = false;
    this.deleteFileModalVisible = true;
  }

  confirmDelete() {
    this.errorDeleteMessage = null;
    this.deleting = true;
    switch (this.storageType) {
      case 'well':
        this.callApiForWellFileDelete();
        break;
      default:
        break;
    }
  }

  callApiForWellFileDelete() {
    this.apiService.deleteFileForWell(this.wellID, this.file.fileName).subscribe({
      next: data => {
        this.deleting = false;
        this.fileDeleteSucceeded.emit(this.file);
        this.dismissAllModals();
      },
      error: err => {
        console.log(err);
        this.errorDeleteMessage = (err.error || 'An error occurred while attempting to delete the file.');
        this.deleting = false;
      }
    });
  }

  displayToastMessage(success: boolean, message: string) {
    if (success) {
      this.messageService.add({ severity: 'success', summary: 'Result', detail: message });
    } else {
      this.messageService.add({ severity: 'error', summary: 'Result', detail: message });
    }
  }
}
